// @ts-nocheck
import * as React from 'react';
import { Dispatch, bindActionCreators, compose } from 'redux';
import actions from '../../redux/actions';
import { connect } from 'react-redux';
import { Alert } from 'reactstrap';
import { StyleState } from '../../utils/styles';
import { LProps, withLocalization } from '../../utils/wrappers';
import { WaitingPageContent } from '../../shared/WaitingPage';
import { asPage } from '../../utils/wrappers/asPage';
import { Link } from 'react-router-dom';
import { makeSimpleRoutePath, Routes } from '../../utils/routerHelper';
import { State, Modals } from '../../redux/state';
import { calculatorAvailablePoints } from '../../redux/selectors';
import StoryPoints from '../../shared/StoryPoints';
import CalculatorShareModal from '../../shared/CalculatorShareModal';
import CalculatorProgressBanner from '../../shared/CalculatorProgressBanner';
import {
  allCalculatorPagesVisited,
  applyCalculatorProgressUpdate,
  calculatorProgressReadyForPoints,
  CalculatorProgressState,
  createCalculatorProgressState
} from '../../utils/calculatorProgress';
import {
  attachCalculatorWindowMessageListener,
  CALCULATOR_IFRAME_ID,
  CalculatorIframeMessage,
  isCalculatorTakeActionMessage,
  scheduleCalculatorWidgetInitialize
} from '../../utils/calculatorIframeMessages';
import {
  buildChallengeCalculatorIframeSrc,
  CALCULATOR_HEIGHT_MESSAGE,
  clampCalculatorEmbedHeight,
  resolveCalculatorEmbedBaseUrl
} from '../../utils/calculatorEmbedUrl';
import { calculatorEmbedLocationForCompetition } from '../../utils/calculatorEmbedLocation';

interface Props {
  token: string;
  competitionName: string;
  availablePoints: number;
  modal: Modals;
  showCalculatorShareModal: (footprintHint?: number) => void;
  updateCalculatorSnapshot: (raw: Record<string, unknown>) => void;
  updateFootprint: (footprint: number) => void;
  showToast: (toastData: { title: string; message: string; style: StyleState }) => void;
}

const containerID = 'cc__calculator';
const calculatorEmbedBaseUrl = resolveCalculatorEmbedBaseUrl();

interface CalculatorPageState {
  isCalculatorReady: boolean;
  error: boolean;
  availablePoints: number;
  progress: CalculatorProgressState;
  iframeHeight: number | null;
}

const initialState: CalculatorPageState = {
  isCalculatorReady: false,
  error: !calculatorEmbedBaseUrl,
  availablePoints: 500,
  progress: createCalculatorProgressState(),
  iframeHeight: null
};

class CalculatorPage extends React.Component<LProps<Props>> {
  private detachWindowMessageListener: (() => void) | null = null;
  private cancelWidgetInitialize: (() => void) | null = null;
  private lastFootprintMt = 0;
  private sharePromptShown = false;
  private requirementsToastShown = false;
  private calculatorEmbedOrigin: string | null = calculatorEmbedBaseUrl
    ? calculatorEmbedOriginFromBase(calculatorEmbedBaseUrl)
    : null;

  public state = initialState;

  private calculatorIframeSrc = (): string => {
    if (!calculatorEmbedBaseUrl) {
      return '';
    }
    const embedLocation = calculatorEmbedLocationForCompetition(this.props.competitionName);
    return buildChallengeCalculatorIframeSrc(calculatorEmbedBaseUrl, embedLocation);
  }

  private updateProgress = (message: CalculatorIframeMessage) => {
    this.setState((prevState) => ({
      progress: applyCalculatorProgressUpdate(prevState.progress, message)
    }));
  }

  private maybePromptShareModal = () => {
    const { availablePoints, modal, t, showToast } = this.props;
    const { progress } = this.state;
    if (availablePoints === 0 || this.sharePromptShown || modal === Modals.CalculatorShare) {
      return;
    }
    if (!calculatorProgressReadyForPoints(progress)) {
      if (!this.requirementsToastShown && allCalculatorPagesVisited(progress)) {
        this.requirementsToastShown = true;
        showToast({
          title: t('Customize each page'),
          message: t('Change at least one value on every calculator page, then submit a short reflection to earn points.'),
          style: StyleState.Warning
        });
      }
      return;
    }
    this.props.showCalculatorShareModal(this.lastFootprintMt);
  }

  private handleCalculatorMessage = (message: CalculatorIframeMessage) => {
    if (!message || !message.type) {
      return;
    }

    if (message.type === 'inputs:update' || message.type === 'page:visited') {
      this.updateProgress(message);
    }

    if (message.type === 'onChange' && message.data && message.data.value) {
      this.lastFootprintMt = Number(message.data.value) || 0;
      this.props.updateCalculatorSnapshot(message.data);
      this.props.updateFootprint(this.lastFootprintMt);
      this.maybePromptShareModal();
      return;
    }

    if (message.data && (message.data.user_footprint || message.data.userFootprint)) {
      this.props.updateCalculatorSnapshot(message.data as Record<string, unknown>);
      return;
    }

    if (message.type === 'inputs:update' && message.data) {
      this.props.updateCalculatorSnapshot(message.data as Record<string, unknown>);
      return;
    }

    if (this.props.availablePoints === 0 || this.sharePromptShown) {
      return;
    }

    if (isCalculatorTakeActionMessage(message)) {
      this.maybePromptShareModal();
    }
  }

  public componentDidUpdate(prevProps: LProps<Props>, prevState: CalculatorPageState) {
    if (this.props.modal === Modals.CalculatorShare && prevProps.modal !== Modals.CalculatorShare) {
      this.sharePromptShown = true;
    }
    if (
      !calculatorProgressReadyForPoints(prevState.progress) &&
      calculatorProgressReadyForPoints(this.state.progress)
    ) {
      this.maybePromptShareModal();
    }
  }

  private handleCalculatorHeightMessage = (event: MessageEvent) => {
    if (!event.data || event.data.type !== CALCULATOR_HEIGHT_MESSAGE) {
      return;
    }
    if (typeof event.data.height !== 'number') {
      return;
    }
    if (this.calculatorEmbedOrigin && event.origin !== this.calculatorEmbedOrigin) {
      return;
    }
    this.setState({ iframeHeight: clampCalculatorEmbedHeight(event.data.height) });
  }

  private onNextCalculatorIframeLoad = () => {
    this.setState({ isCalculatorReady: true });
    const initOptions: Record<string, unknown> = {
      container_id: containerID,
      hideUserPlatform: true,
      includeTakeAction: false
    };
    if (this.props.token) {
      initOptions.token = this.props.token;
    }
    this.cancelWidgetInitialize = scheduleCalculatorWidgetInitialize(containerID, initOptions);
  }

  public componentDidMount() {
    if (!calculatorEmbedBaseUrl) {
      return;
    }
    this.detachWindowMessageListener = attachCalculatorWindowMessageListener(this.handleCalculatorMessage);
    window.addEventListener('message', this.handleCalculatorHeightMessage);
  }

  public componentWillUnmount() {
    window.removeEventListener('message', this.handleCalculatorHeightMessage);
    if (this.cancelWidgetInitialize) {
      this.cancelWidgetInitialize();
      this.cancelWidgetInitialize = null;
    }
    if (this.detachWindowMessageListener) {
      this.detachWindowMessageListener();
      this.detachWindowMessageListener = null;
    }
    const container = document.getElementById(containerID);
    if (container) {
      container.innerHTML = '';
    }
  }

  public render() {
    const { error, isCalculatorReady, progress, iframeHeight } = this.state;
    const calculatorIframeSrc = this.calculatorIframeSrc();
    const { t, availablePoints, formatNumber, modal } = this.props;
    const requirementsMet = calculatorProgressReadyForPoints(progress);
    const actionCompleted = availablePoints === 0;
    if (error) {
      return (
        <Alert
          color={StyleState.Danger}
        >{t('It appears we had an issue loading the calculator. Please try again later.')}</Alert>
      );
    }
    if (!calculatorIframeSrc) {
      return (
        <Alert color={StyleState.Danger}>
          {t('Calculator URL is not configured. Set REACT_APP_CALCULATOR_URL and rebuild.')}
        </Alert>
      );
    }
    return (
      <div id="cc__calculator_page">
        <div style={{ marginBottom: '0.75rem' }}>
          <Link to={makeSimpleRoutePath(Routes.Dashboard, t)}>{t('Return to dashboard')}</Link>
        </div>
        <StoryPoints />
        <CalculatorShareModal progressReadyForPoints={requirementsMet && !actionCompleted} />
        <div style={{ marginBottom: '1rem' }}>
          <CalculatorProgressBanner
            progress={progress}
            requirementsMet={requirementsMet}
            actionCompleted={actionCompleted}
            pointsEarned={500}
          />
        </div>
        {!actionCompleted && <h4>{formatNumber(availablePoints, 0)}&nbsp;{t('points')}</h4>}
        {actionCompleted &&
        <h4 className="cc__lighttext">
          <img style={{ width: '36px' }} src="/assets/story/checkmark@2x.png" alt={t('action taken')} />&nbsp;
          {t('Complete')}
        </h4>}
        {requirementsMet && !actionCompleted && modal !== Modals.CalculatorShare &&
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => this.props.showCalculatorShareModal(this.lastFootprintMt)}
            >{t('Submit reflection to earn points')}</button>
          </div>
        }
        {!isCalculatorReady &&
          <div style={{ marginBottom: '1rem' }}>
            <WaitingPageContent />
          </div>
        }
        <div
          id={containerID}
          style={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'visible',
            padding: '8px 20px 24px',
            boxSizing: 'border-box'
          }}
        >
          <iframe
              id={CALCULATOR_IFRAME_ID}
              title={t('CoolClimate Calculator')}
              src={calculatorIframeSrc}
              scrolling="no"
              allow="clipboard-write; web-share"
              style={{
                width: '100%',
                maxWidth: '100%',
                border: 0,
                display: 'block',
                minHeight: 640,
                height: iframeHeight ? `${iframeHeight}px` : undefined,
                visibility: isCalculatorReady ? 'visible' : 'hidden'
              }}
              onLoad={() => this.onNextCalculatorIframeLoad()}
            />
        </div>
      </div>);
  }
}

function calculatorEmbedOriginFromBase(baseUrl: string): string {
  try {
    return new URL(baseUrl).origin;
  } catch (_err) {
    return '';
  }
}

const mapStateToProps = (state: State): Partial<Props> => ({
  token: state.token,
  competitionName: (state.competition && state.competition.name) || '',
  availablePoints: calculatorAvailablePoints(state),
  modal: state.modal
});
const mapDispatchToProps = (dispatch: Dispatch): Partial<Props> => bindActionCreators({
  showCalculatorShareModal: actions.showCalculatorShareModal,
  updateCalculatorSnapshot: actions.updateCalculatorSnapshot,
  updateFootprint: actions.updateFootprint,
  showToast: actions.toast
}, dispatch);

export default asPage({})(compose(
  withLocalization('calculator'),
  connect(mapStateToProps, mapDispatchToProps)
)(CalculatorPage) as React.SFC);
